/**
 * 本地存储服务
 * @module services/storage
 */

import { sanitizeSensitiveWorkspaceXml } from '@/features/blockly/createWorkspace';
import { parseSb3ExtensionsPayload, type Sb3ProjectPayload } from '@/features/scratch/sb3Payload';
import { DEFAULT_PROJECT_VERSION, generateId, isValidSemver, nowISO, safeJsonParse } from '@/utils';
import type { Project, ProjectMeta, TemplateId, LanguageId } from '@/types';
import { getTemplate } from './templates';

/** 存储键前缀 */
const STORAGE_PREFIX = 'yiblock2_';
const PROJECTS_KEY = `${STORAGE_PREFIX}projects`;
const SETTINGS_KEY = `${STORAGE_PREFIX}settings`;
const TEMP_PROJECT_KEY_PREFIX = `${STORAGE_PREFIX}temp_`;

function isQuotaExceededError(error: unknown): boolean {
  return error instanceof Error && (
    error.name === 'QuotaExceededError'
    || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
  );
}

export class LocalStorageQuotaError extends Error {
  constructor() {
    super('本地存储空间不足，请通过本地存储管理器删除不需要的本地项目或恢复缓存后重试。');
    this.name = 'LocalStorageQuotaError';
  }
}

export function isLocalStorageQuotaError(error: unknown): error is LocalStorageQuotaError {
  return error instanceof LocalStorageQuotaError;
}

export interface LocalStorageProjectEntry {
  id: string;
  name: string;
  updatedAt: string;
  kind: 'project' | 'temp';
  sizeBytes: number;
}

/** 应用设置 */
export interface AppSettings {
  /** 当前语言 */
  language: LanguageId;
  /** 界面语言 */
  uiLanguage: 'auto' | 'zh-CN' | 'en-US';
  /** 网格启用 */
  gridEnabled: boolean;
  /** 网格对齐 */
  gridSnap: boolean;
  /** 自动保存 */
  autoSave: boolean;
  /** 自动保存间隔（毫秒） */
  autoSaveInterval: number;
  /** 代码面板是否固定在右侧栏 */
  codeSidebarPinned: boolean;
}

/** 默认设置 */
const DEFAULT_SETTINGS: AppSettings = {
  language: 'js',
  uiLanguage: 'auto',
  gridEnabled: true,
  gridSnap: true,
  autoSave: true,
  autoSaveInterval: 2000,
  codeSidebarPinned: false,
};

class StorageService {
  private removeStaleTempProjects(projects: Record<string, Project>): boolean {
    const tempKeys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => Boolean(key?.startsWith(TEMP_PROJECT_KEY_PREFIX)));
    let removed = false;

    tempKeys.forEach((key) => {
      const tempProject = safeJsonParse<Project | null>(localStorage.getItem(key) || '', null);
      if (!tempProject) return;
      const savedProject = projects[tempProject.meta.id];
      if (savedProject && tempProject.meta.updatedAt <= savedProject.meta.updatedAt) {
        localStorage.removeItem(key);
        removed = true;
      }
    });

    return removed;
  }

  private throwQuotaExceededError(): never {
    window.dispatchEvent(new CustomEvent('open-local-storage-manager', {
      detail: { reason: 'quota-exceeded' },
    }));
    throw new LocalStorageQuotaError();
  }

  getLocalStorageProjectEntries(): LocalStorageProjectEntry[] {
    const projects = safeJsonParse<Record<string, Project>>(localStorage.getItem(PROJECTS_KEY) || '{}', {});
    const entries: LocalStorageProjectEntry[] = Object.values(projects).map((project) => ({
      id: project.meta.id,
      name: project.meta.name || '未命名项目',
      updatedAt: project.meta.updatedAt,
      kind: 'project',
      sizeBytes: new Blob([JSON.stringify(project)]).size,
    }));

    Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => Boolean(key?.startsWith(TEMP_PROJECT_KEY_PREFIX)))
      .forEach((key) => {
        const value = localStorage.getItem(key) || '';
        const project = safeJsonParse<Project | null>(value, null);
        if (!project) return;
        entries.push({
          id: project.meta.id,
          name: project.meta.name || '未命名项目',
          updatedAt: project.meta.updatedAt,
          kind: 'temp',
          sizeBytes: new Blob([value]).size,
        });
      });

    return entries.sort((left, right) => right.sizeBytes - left.sizeBytes || Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
  }

  removeLocalStorageProjectEntry(entry: Pick<LocalStorageProjectEntry, 'id' | 'kind'>): void {
    if (entry.kind === 'temp') {
      this.clearTempProject(entry.id);
      return;
    }

    this.deleteProject(entry.id);
    this.clearTempProject(entry.id);
  }

  hasProject(id: string): boolean {
    const data = localStorage.getItem(PROJECTS_KEY);
    if (!data) return false;

    const projects = safeJsonParse<Record<string, Project>>(data, {});
    return Boolean(projects[id]);
  }

  /**
   * 获取所有项目元数据列表
   */
  getProjectList(): ProjectMeta[] {
    const data = localStorage.getItem(PROJECTS_KEY);
    if (!data) return [];

    const projects = safeJsonParse<Record<string, Project>>(data, {});
    return Object.values(projects)
      .map(p => p.meta)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  /** 获取全部本地项目，用于按来源查找已下载作品。 */
  getProjects(): Project[] {
    const data = localStorage.getItem(PROJECTS_KEY);
    if (!data) return [];

    return Object.values(safeJsonParse<Record<string, Project>>(data, {}));
  }

  /**
   * 获取项目
   */
  getProject(id: string): Project | null {
    const data = localStorage.getItem(PROJECTS_KEY);
    if (!data) return null;

    const projects = safeJsonParse<Record<string, Project>>(data, {});
    return projects[id] || null;
  }

  /**
   * 保存项目
   */
  saveProject(project: Project): void {
    const data = localStorage.getItem(PROJECTS_KEY);
    const projects = safeJsonParse<Record<string, Project>>(data || '{}', {});

    const savedProject: Project = {
      ...project,
      workspaceXml: sanitizeSensitiveWorkspaceXml(project.workspaceXml),
      meta: {
        ...project.meta,
        updatedAt: nowISO(),
        version: isValidSemver(project.meta.version)
          ? project.meta.version
          : DEFAULT_PROJECT_VERSION,
      },
    };
    projects[savedProject.meta.id] = savedProject;

    const serializedProjects = JSON.stringify(projects);
    try {
      localStorage.setItem(PROJECTS_KEY, serializedProjects);
    } catch (error) {
      if (!isQuotaExceededError(error)) throw error;

      this.removeStaleTempProjects(projects);
      try {
        localStorage.setItem(PROJECTS_KEY, serializedProjects);
      } catch (retryError) {
        if (isQuotaExceededError(retryError)) this.throwQuotaExceededError();
        throw retryError;
      }
    }
  }

  /**
   * 保存临时项目（用于自动恢复）
   */
  saveTempProject(project: Project): void {
    const key = `${TEMP_PROJECT_KEY_PREFIX}${project.meta.id}`;
    const serializedProject = JSON.stringify({
      ...project,
      workspaceXml: sanitizeSensitiveWorkspaceXml(project.workspaceXml),
    });

    try {
      localStorage.setItem(key, serializedProject);
    } catch (error) {
      if (!isQuotaExceededError(error)) throw error;

      const projects = safeJsonParse<Record<string, Project>>(localStorage.getItem(PROJECTS_KEY) || '{}', {});
      this.removeStaleTempProjects(projects);
      try {
        localStorage.setItem(key, serializedProject);
      } catch (retryError) {
        if (isQuotaExceededError(retryError)) this.throwQuotaExceededError();
        throw retryError;
      }
    }
  }

  /**
   * 获取临时项目
   */
  getTempProject(id: string): Project | null {
    const key = `${TEMP_PROJECT_KEY_PREFIX}${id}`;
    const data = localStorage.getItem(key);
    if (!data) return null;
    return safeJsonParse<Project | null>(data, null);
  }

  /**
   * 清除临时项目
   */
  clearTempProject(id: string): void {
    const key = `${TEMP_PROJECT_KEY_PREFIX}${id}`;
    localStorage.removeItem(key);
  }

  /**
   * 创建新项目
   */
  createProject(name: string, templateId: TemplateId = 'blank'): Project {
    const template = getTemplate(templateId);
    const now = nowISO();

    const project: Project = {
      meta: {
        id: generateId(),
        name: name || '未命名项目',
        description: '',
        createdAt: now,
        updatedAt: now,
        templateId,
        language: 'js',
        version: DEFAULT_PROJECT_VERSION,
      },
      workspaceXml: template.workspaceXml,
    };

    this.saveProject(project);
    return project;
  }

  createDraftProject(
    name: string,
    templateId: TemplateId = 'blank',
    metaOverrides?: Partial<Pick<ProjectMeta, 'description' | 'thumbnail' | 'version'>>
  ): Project {
    const template = getTemplate(templateId);
    const now = nowISO();
    const normalizedVersion = String(metaOverrides?.version || '').trim();

    const project: Project = {
      meta: {
        id: generateId(),
        name: name || '未命名项目',
        description: String(metaOverrides?.description || '').trim(),
        createdAt: now,
        updatedAt: now,
        thumbnail: String(metaOverrides?.thumbnail || '').trim(),
        templateId,
        language: 'js',
        version: isValidSemver(normalizedVersion) ? normalizedVersion : DEFAULT_PROJECT_VERSION,
      },
      workspaceXml: template.workspaceXml,
    };

    this.saveTempProject(project);
    return project;
  }

  /**
   * 从工作区 XML 创建项目
   */
  createProjectFromWorkspace(
    name: string,
    workspaceXml: string,
    templateId: TemplateId = 'blank'
  ): Project {
    const now = nowISO();
    const project: Project = {
      meta: {
        id: generateId(),
        name: name || '未命名项目',
        description: '',
        createdAt: now,
        updatedAt: now,
        templateId,
        language: 'js',
        version: DEFAULT_PROJECT_VERSION,
      },
      workspaceXml,
    };

    this.saveProject(project);
    return project;
  }

  /**
   * 删除项目
   */
  deleteProject(id: string): boolean {
    const data = localStorage.getItem(PROJECTS_KEY);
    if (!data) return false;

    const projects = safeJsonParse<Record<string, Project>>(data, {});
    if (!projects[id]) return false;

    delete projects[id];
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
    this.clearTempProject(id);
    return true;
  }

  /**
   * 更新项目元数据
   */
  updateProjectMeta(id: string, updates: Partial<Pick<ProjectMeta, 'name' | 'description' | 'thumbnail' | 'version'>>): Project | null {
    const data = localStorage.getItem(PROJECTS_KEY);
    if (!data) return null;

    const projects = safeJsonParse<Record<string, Project>>(data, {});
    const project = projects[id];
    if (!project) return null;

    const nextMeta: ProjectMeta = {
      ...project.meta,
      ...updates,
      updatedAt: nowISO(),
    };

    const updatedProject: Project = {
      ...project,
      meta: nextMeta,
    };

    this.saveProject(updatedProject);
    return this.getProject(id);
  }

  /**
   * 导入项目
   */
  importProject(projectData: string): { success: boolean; project?: Project; error?: string } {
    try {
      const parsed = JSON.parse(projectData) as Project | Sb3ProjectPayload;
      const nativePayload = !('meta' in parsed) && typeof parsed.workspaceXml === 'string'
        ? parsed as Sb3ProjectPayload & { workspaceXml: string }
        : null;
      const importedVersion = nativePayload?.version ?? '';
      const imported: Project = nativePayload
        ? {
            meta: {
              id: generateId(),
              name: String(nativePayload.name || '未命名项目').trim() || '未命名项目',
              description: String(nativePayload.description || '').trim(),
              createdAt: nowISO(),
              updatedAt: nowISO(),
              thumbnail: String(nativePayload.thumbnail || '').trim(),
              templateId: 'blank',
              language: 'js',
              version: isValidSemver(importedVersion) ? importedVersion : DEFAULT_PROJECT_VERSION,
            },
            workspaceXml: nativePayload.workspaceXml,
            extensions: parseSb3ExtensionsPayload(nativePayload.extensions),
          }
        : parsed as Project;

      // 验证基本结构
      if (!imported.meta?.id || !imported.workspaceXml) {
        return { success: false, error: '无效的项目文件格式' };
      }

      // 检查是否存在同ID项目
      const existing = this.getProject(imported.meta.id);
      if (existing) {
        // 生成新ID
        imported.meta.id = generateId();
        imported.meta.name = `${imported.meta.name} (副本)`;
      }

      imported.meta.version = isValidSemver(imported.meta.version)
        ? imported.meta.version
        : DEFAULT_PROJECT_VERSION;

      imported.meta.updatedAt = nowISO();
      this.saveProject(imported);

      return { success: true, project: imported };
    } catch {
      return { success: false, error: '项目文件解析失败' };
    }
  }

  /**
   * 导出项目
   */
  exportProject(id: string): string | null {
    const project = this.getProject(id);
    if (!project) return null;
    return JSON.stringify({
      ...project,
      workspaceXml: sanitizeSensitiveWorkspaceXml(project.workspaceXml),
    }, null, 2);
  }

  /**
   * 获取设置
   */
  getSettings(): AppSettings {
    const data = localStorage.getItem(SETTINGS_KEY);
    if (!data) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...safeJsonParse<Partial<AppSettings>>(data, {}) };
  }

  /**
   * 保存设置
   */
  saveSettings(settings: Partial<AppSettings>): void {
    const current = this.getSettings();
    const merged = { ...current, ...settings };
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(merged));
  }

  /**
   * 清空所有数据（危险操作）
   */
  clearAll(): void {
    localStorage.removeItem(PROJECTS_KEY);
    localStorage.removeItem(SETTINGS_KEY);
  }
}

export const storage = new StorageService();
export default storage;
